<template>
  <div class="space-y-2">
    <div
      v-for="(node, index) in document.nodes"
      :key="node.id"
      tabindex="0"
      class="rounded border bg-slate-800/60 p-2 outline-none"
      :class="selectedId === node.id ? 'border-cyan-400' : 'border-slate-600'"
      @click="selectedId = node.id"
      @keydown="handleKeydown($event, index)">
      <textarea
        v-if="node.type === 'text'"
        v-model="node.text"
        rows="2"
        placeholder="Text to speak"
        class="w-full rounded border bg-slate-900 border-slate-600 p-2 text-sm text-gray-100" />
      <p
        v-if="node.type === 'text' && ['{{', '{%', '{#'].some(marker => node.text.includes(marker))"
        class="mt-1 text-xs text-red-400">
        Template syntax is not allowed in ordinary text. Insert a value block instead.
      </p>

      <div v-else-if="node.type === 'entity_state'" class="space-y-1.5">
        <div class="text-xs font-medium text-cyan-300">Entity value · {{ node.label || node.entityId || 'Not selected' }}</div>
        <EntitySelector
          :modelValue="node.entityId"
          :settings="settings"
          @update:modelValue="selectEntity(node, $event)" />
        <form-input v-model="node.label" type="text" placeholder="Display name" class="w-full text-sm" />
        <form-input v-model="node.fallback" type="text" placeholder="When unavailable (optional)" class="w-full text-sm" />
        <p v-if="!node.entityId" class="text-xs text-red-400">Select an entity.</p>
      </div>

      <div v-else-if="node.type === 'entity_attribute'" class="space-y-1.5">
        <div class="text-xs font-medium text-cyan-300">Entity attribute · {{ node.label || node.entityId || 'Not selected' }}</div>
        <EntitySelector
          :modelValue="node.entityId"
          :settings="settings"
          @update:modelValue="selectEntity(node, $event)" />
        <form-select v-model="node.attribute" class="w-full text-sm">
          <option value="">— attribute —</option>
          <option v-for="attribute in attributeOptions(node)" :key="attribute" :value="attribute">{{ attribute }}</option>
        </form-select>
        <form-input v-model="node.label" type="text" placeholder="Display name" class="w-full text-sm" />
        <form-input v-model="node.fallback" type="text" placeholder="When unavailable (optional)" class="w-full text-sm" />
        <p v-if="!node.entityId || !node.attribute" class="text-xs text-red-400">Select an entity and attribute.</p>
      </div>

      <div v-else-if="node.type === 'datetime'" class="space-y-1.5">
        <div class="text-xs font-medium text-cyan-300">Time or date · {{ node.label }}</div>
        <form-select v-model="node.value" class="w-full text-sm" @update:modelValue="updateDateLabel(node, $event)">
          <option v-for="option in DATE_TIME_VALUES" :key="option.value" :value="option.value">{{ option.label }}</option>
        </form-select>
        <form-input v-model="node.label" type="text" placeholder="Display name" class="w-full text-sm" />
      </div>

      <details v-else-if="node.type === 'condition'" open class="space-y-2">
        <summary class="cursor-pointer text-xs font-medium text-cyan-300">Conditional text</summary>
        <div class="mt-2 space-y-1.5">
          <EntitySelector
            :modelValue="node.condition.source.entityId"
            :settings="settings"
            @update:modelValue="selectConditionEntity(node, $event)" />
          <form-select
            :modelValue="node.condition.source.type"
            class="w-full text-sm"
            @update:modelValue="setConditionSourceType(node, $event)">
            <option value="entity_state">State</option>
            <option value="entity_attribute">Attribute</option>
          </form-select>
          <form-select
            v-if="node.condition.source.type === 'entity_attribute'"
            v-model="node.condition.source.attribute"
            class="w-full text-sm">
            <option value="">— attribute —</option>
            <option v-for="attribute in conditionAttributes(node)" :key="attribute" :value="attribute">{{ attribute }}</option>
          </form-select>
          <form-select v-model="node.condition.operator" class="w-full text-sm">
            <option v-for="operator in conditionOperators(node)" :key="operator.value" :value="operator.value">
              {{ operator.label }}
            </option>
          </form-select>
          <form-input
            v-if="!['is_on', 'is_off'].includes(node.condition.operator)"
            v-model="node.condition.value"
            :type="numericCondition(node) ? 'number' : 'text'"
            placeholder="Comparison value"
            class="w-full text-sm" />
          <p v-if="conditionInvalid(node)" class="text-xs text-red-400">Complete the condition.</p>

          <form-label class="block text-xs text-gray-400">If true</form-label>
          <TemplateDocumentEditor :document="node.whenTrue" :settings="settings" nested />
          <form-label class="block text-xs text-gray-400">Otherwise (optional)</form-label>
          <TemplateDocumentEditor :document="node.whenFalse" :settings="settings" nested />
        </div>
      </details>

      <div class="mt-2 flex justify-end gap-2 text-xs">
        <button class="text-cyan-400" @click="insert(index + 1)">Insert below</button>
        <button :disabled="index === 0" class="text-gray-400 disabled:opacity-30" @click="move(index, -1)">↑</button>
        <button :disabled="index === document.nodes.length - 1" class="text-gray-400 disabled:opacity-30" @click="move(index, 1)">↓</button>
        <button class="text-red-400" @click="remove(index)">Remove</button>
      </div>
    </div>

    <div class="flex gap-1.5">
      <form-select v-model="insertType" class="flex-1 text-sm">
        <option value="text">Text</option>
        <option value="entity_state">Entity value</option>
        <option value="entity_attribute">Entity attribute</option>
        <option value="datetime">Current time or date</option>
        <option value="condition">Conditional text</option>
      </form-select>
      <button class="rounded bg-slate-600 px-2 text-xs text-white hover:bg-slate-500" @click="insert()">+ Insert value</button>
    </div>
  </div>
</template>

<script setup>
defineOptions({ name: 'TemplateDocumentEditor' });

import { reactive, ref, watch, onMounted } from 'vue';
import EntitySelector from '../EntitySelector.vue';
import { getEntityInfo } from '../../../services/homeAssistantService.js';
import {
  DATE_TIME_VALUES,
  createTextNode,
  createEntityStateNode,
  createEntityAttributeNode,
  createDateTimeNode,
  createConditionNode,
} from '../../../automation/templateDocument.js';

const props = defineProps({
  document: { type: Object, required: true },
  settings: { type: Object, required: true },
  nested: { type: Boolean, default: false },
});
const emit = defineEmits(['change']);

const insertType = ref('text');
const selectedId = ref(null);
const entityInfo = reactive({});

watch(() => props.document, () => emit('change'), { deep: true });

function collectEntityIds(document, ids = new Set()) {
  for (const node of document?.nodes ?? []) {
    if (node.entityId) ids.add(node.entityId);
    if (node.type === 'condition') {
      if (node.condition?.source?.entityId) ids.add(node.condition.source.entityId);
      collectEntityIds(node.whenTrue, ids);
      collectEntityIds(node.whenFalse, ids);
    }
  }
  return ids;
}

onMounted(() => {
  for (const entityId of collectEntityIds(props.document)) {
    loadEntity(entityId).then(info => applyFriendlyLabels(props.document, entityId, info));
  }
});

function applyFriendlyLabels(document, entityId, info) {
  for (const node of document?.nodes ?? []) {
    if (node.entityId === entityId && (!node.label || node.label === entityId || node.label.startsWith(`${entityId} ·`))) {
      node.label = node.type === 'entity_attribute'
        ? `${info?.attributes?.friendly_name ?? entityId} · ${node.attribute}`
        : info?.attributes?.friendly_name ?? entityId;
    }
    if (node.type === 'condition') {
      applyFriendlyLabels(node.whenTrue, entityId, info);
      applyFriendlyLabels(node.whenFalse, entityId, info);
    }
  }
}

async function loadEntity(entityId) {
  if (!entityId || entityInfo[entityId]) return entityInfo[entityId];
  try {
    entityInfo[entityId] = await getEntityInfo(props.settings, entityId);
  } catch {
    entityInfo[entityId] = null;
  }
  return entityInfo[entityId];
}

async function selectEntity(node, entityId) {
  node.entityId = entityId;
  const info = await loadEntity(entityId);
  if (!node.label || node.label === node.entityId) {
    node.label = info?.attributes?.friendly_name ?? entityId;
  }
  if (node.type === 'entity_attribute' && !attributeOptions(node).includes(node.attribute)) {
    node.attribute = '';
  }
}

async function selectConditionEntity(node, entityId) {
  node.condition.source.entityId = entityId;
  await loadEntity(entityId);
  if (node.condition.source.type === 'entity_attribute' &&
      !conditionAttributes(node).includes(node.condition.source.attribute)) {
    node.condition.source.attribute = '';
  }
  const allowed = conditionOperators(node);
  if (!allowed.some(option => option.value === node.condition.operator)) {
    node.condition.operator = allowed[0].value;
  }
}

function setConditionSourceType(node, type) {
  node.condition.source.type = type;
  if (type === 'entity_attribute') node.condition.source.attribute ??= '';
  const allowed = conditionOperators(node);
  if (!allowed.some(option => option.value === node.condition.operator)) {
    node.condition.operator = allowed[0].value;
  }
}

function attributeOptions(node) {
  const values = Object.keys(entityInfo[node.entityId]?.attributes ?? {});
  if (node.attribute && !values.includes(node.attribute)) values.push(node.attribute);
  return values.sort();
}

function conditionAttributes(node) {
  const source = node.condition.source;
  const values = Object.keys(entityInfo[source.entityId]?.attributes ?? {});
  if (source.attribute && !values.includes(source.attribute)) values.push(source.attribute);
  return values.sort();
}

const GENERIC_OPERATORS = [
  { value: 'equals', label: 'equals' },
  { value: 'not_equals', label: 'does not equal' },
  { value: 'contains', label: 'contains' },
];
const NUMERIC_OPERATORS = [
  { value: 'equals', label: 'equals' },
  { value: 'not_equals', label: 'does not equal' },
  { value: 'greater_than', label: 'is greater than' },
  { value: 'greater_or_equal', label: 'is at least' },
  { value: 'less_than', label: 'is less than' },
  { value: 'less_or_equal', label: 'is at most' },
];
const BINARY_OPERATORS = [
  { value: 'is_on', label: 'is on / open' },
  { value: 'is_off', label: 'is off / closed' },
];

function entityDomain(node) {
  return node.condition.source.entityId.split('.')[0];
}

function numericCondition(node) {
  const info = entityInfo[node.condition.source.entityId];
  return entityDomain(node) === 'number' || entityDomain(node) === 'input_number' ||
    info?.attributes?.device_class === 'temperature' || info?.attributes?.unit_of_measurement;
}

function conditionOperators(node) {
  if (entityDomain(node) === 'binary_sensor' || entityDomain(node) === 'input_boolean') return BINARY_OPERATORS;
  if (numericCondition(node)) return NUMERIC_OPERATORS;
  return GENERIC_OPERATORS;
}

function conditionInvalid(node) {
  const condition = node.condition;
  return !condition.source.entityId ||
    (condition.source.type === 'entity_attribute' && !condition.source.attribute) ||
    !condition.operator ||
    (!['is_on', 'is_off'].includes(condition.operator) && condition.value === '');
}

function updateDateLabel(node, value) {
  node.value = value;
  node.label = DATE_TIME_VALUES.find(option => option.value === value)?.label ?? value;
}

function insert(index = props.document.nodes.length) {
  const factories = {
    text: createTextNode,
    entity_state: createEntityStateNode,
    entity_attribute: createEntityAttributeNode,
    datetime: createDateTimeNode,
    condition: createConditionNode,
  };
  const node = factories[insertType.value]();
  props.document.nodes.splice(index, 0, node);
  selectedId.value = node.id;
}

function remove(index) {
  props.document.nodes.splice(index, 1);
  const previous = props.document.nodes[index - 1];
  const current = props.document.nodes[index];
  if (previous?.type === 'text' && current?.type === 'text') {
    previous.text += current.text;
    props.document.nodes.splice(index, 1);
  }
  selectedId.value = null;
}

function move(index, direction) {
  const target = index + direction;
  if (target < 0 || target >= props.document.nodes.length) return;
  const [node] = props.document.nodes.splice(index, 1);
  props.document.nodes.splice(target, 0, node);
}

function handleKeydown(event, index) {
  if (!['Delete', 'Backspace'].includes(event.key)) return;
  if (['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target.tagName)) return;
  event.preventDefault();
  remove(index);
}
</script>
